import { useLayoutEffect, useRef, useState } from "react";
import { Trash2 } from "lucide-react";
import VoiceWaveform from "./VoiceWaveform";

// Presentation only: the recorder has already discarded its stream and bytes.
// Each sample is pulled in separately: the near end enters first, while the
// remaining ribbon bends and shortens. No whole-wave translate/scale/fade.
export function suctionPoints(samples, source, opening, progress) {
  if (progress >= 1) return [];
  const padded = [...Array(Math.max(0, 64 - samples.length)).fill(0), ...samples];
  return padded.map((value, i) => {
    const t = Math.max(0, Math.min(1, (i * 4 - 12) / 240));
    const pull = Math.max(0, Math.min(1, (progress - t * .32) / .68));
    const ease = pull * pull * (3 - 2 * pull);
    return { x: (source.x + t * source.width) * (1 - ease) + opening.x * ease,
      y: (source.y + source.height / 2) * (1 - ease) + opening.y * ease
        - value * source.height * 15 / 36 * (1 - ease), entered: pull >= 1 };
  }).filter(point => !point.entered);
}

export default function VoiceDiscardAnimation({ waveform, level, label, locked = false, source }) {
  const root = useRef(null);
  const [frame, setFrame] = useState(null);
  useLayoutEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) return undefined;
    const panel = root.current?.closest('.pc-voice-panel');
    const bounds = panel?.getBoundingClientRect();
    const trash = panel?.querySelector('.lucide-trash-2')?.getBoundingClientRect();
    const wave = root.current?.querySelector('[data-testid="voice-wave"]')?.getBoundingClientRect();
    if (!bounds || !trash || !wave) return undefined;
    // Hold uses the existing inline wave slot to the right of its Trash.
    // Locked keeps the captured live-wave slot above the unchanged controls.
    const origin = locked && source ? { ...source } : { x: wave.x - bounds.x, y: wave.y - bounds.y, width: wave.width, height: wave.height };
    origin.x -= panel.clientLeft; origin.y -= panel.clientTop;
    // Trash2's opening is y=7 in its 24-unit viewBox, directly under the lid.
    const opening = { x: trash.x - bounds.x - panel.clientLeft + trash.width / 2, y: trash.y - bounds.y - panel.clientTop + trash.height * 7 / 24 };
    const start = performance.now();
    let request;
    const draw = (now) => {
      const progress = Math.max(0, Math.min(1, (now - start - 187) / 442));
      const points = suctionPoints(waveform?.samples || [], origin, opening, progress);
      const path = points.map((p, i) => `${i ? 'L' : 'M'} ${p.x} ${p.y}`).join(' ');
      setFrame({ path, width: panel.clientWidth, height: panel.clientHeight, stage: progress >= 1 ? "swallowed" : progress >= .7 ? "entering" : "pulling" });
      if (progress < 1) request = requestAnimationFrame(draw);
    };
    draw(start);
    return () => cancelAnimationFrame(request);
  }, [source, waveform, locked]);
  return <div ref={root} className="pc-voice-discard" data-locked={locked || undefined} data-testid="voice-discard-animation" role="status">
    <span className="sr-only">{label}</span>
    <div className="pc-voice-discard__visual" aria-hidden="true">
      {!locked && <Trash2 className="pc-voice-discard__trash" size={20} strokeWidth={2} />}
      <div className="pc-voice-discard__wave">
        <VoiceWaveform waveform={waveform} level={level} paused />
      </div>
    </div>
    {frame && <svg className="pc-voice-discard__suction" data-stage={frame.stage} viewBox={`0 0 ${frame.width} ${frame.height}`} preserveAspectRatio="none" aria-hidden="true">
      <path d={frame.path} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>}
  </div>;
}
